import { StorageBucket } from "@excel-center/shared";
import { compressImage, presets } from "@/src/common/media";
import { supabase } from "@/src/common/services/supabase.service";

const BUCKET = StorageBucket.AVATARS;

// Must match the avatars bucket's allowed_mime_types (20260417234538_storage_limits.sql).
const ALLOWED_TYPES: Record<string, string> = {
	"image/webp": "webp",
	"image/jpeg": "jpg",
	"image/png": "png",
};

export class AvatarUploadError extends Error {
	constructor(public readonly code: "unsupported_type") {
		super(code);
		this.name = "AvatarUploadError";
	}
}

async function uploadAvatar(userId: string, file: File): Promise<string> {
	const { file: compressed } = await compressImage(file, presets.avatar);
	const ext = ALLOWED_TYPES[compressed.type];
	if (!ext) throw new AvatarUploadError("unsupported_type");

	const path = `${userId}/avatar-${Date.now()}.${ext}`;
	// upsert requires SELECT/UPDATE storage policies the avatars bucket does
	// not have (the path is unique anyway), and made every upload fail RLS.
	const { error: upErr } = await supabase.storage.from(BUCKET).upload(path, compressed, {
		cacheControl: "3600",
		upsert: false,
		contentType: compressed.type,
	});
	if (upErr) throw new Error(upErr.message);

	const { data } = supabase.storage.from(BUCKET).getPublicUrl(path);
	return data.publicUrl;
}

export const avatarUploadService = {
	async upload(userId: string, file: File): Promise<string> {
		const publicUrl = await uploadAvatar(userId, file);
		const { error: updErr } = await supabase.from("profiles").update({ avatar_url: publicUrl }).eq("id", userId);
		if (updErr) throw new Error(updErr.message);
		return publicUrl;
	},

	async uploadWithoutPersist(userId: string, file: File): Promise<string> {
		return uploadAvatar(userId, file);
	},
};
